/**
 * Bandholz Theme Customizations
 *
 * Global JavaScript customizations layered on top of the Ritual theme.
 * Keep custom behavior isolated from Ritual's original theme scripts.
 *
 * Current customizations:
 * 1. Move custom product-card badges into Ritual card galleries.
 * 2. Copy Ritual's native badge wrapper settings to custom badges.
 * 3. Replace the Header bundle product price with a fixed savings message.
 *
 * Bundle product:
 * • Shopify product ID: 8437895200959
 * • Handle fallback: custom-fragrance-bundle
 *
 * Important:
 * • Do not copy Ritual's native scripts into this file.
 * • Use this file only for Bandholz-specific progressive enhancements.
 * • This file is globally loaded with the defer attribute.
 *
 * -Jenny 7/28/26
 */

(() => {
  'use strict';

  /**
   * Read the configuration rendered by
   * snippets/bandholz-customizations-assets.liquid.
   *
   * @returns {{
   *   bundleProductId: string,
   *   bundleProductHandle: string,
   *   bundleCardMessage: string
   * }}
   */
  function getBandholzConfig() {
    const configElement = document.querySelector(
      '#bandholz-customizations-config'
    );

    const fallbackConfig = {
      bundleProductId: '8437895200959',
      bundleProductHandle: 'custom-fragrance-bundle',
      bundleCardMessage: 'SAVE 25% ON SETS'
    };

    if (!configElement) {
      return fallbackConfig;
    }

    try {
      const parsedConfig = JSON.parse(
        configElement.textContent || '{}'
      );

      return {
        bundleProductId:
          String(parsedConfig.bundleProductId || '') ||
          fallbackConfig.bundleProductId,

        bundleProductHandle:
          parsedConfig.bundleProductHandle ||
          fallbackConfig.bundleProductHandle,

        bundleCardMessage:
          parsedConfig.bundleCardMessage ||
          fallbackConfig.bundleCardMessage
      };
    } catch (error) {
      console.warn(
        'Bandholz customizations: unable to read configuration.',
        error
      );

      return fallbackConfig;
    }
  }

  const config = getBandholzConfig();

  /**
   * Find the position class used by a Ritual product-badge wrapper.
   *
   * Example:
   * product-badges--top-right
   *
   * @param {HTMLElement} wrapper
   * @returns {string}
   */
  function getBadgePositionClass(wrapper) {
    return (
      Array.from(wrapper.classList).find((className) =>
        className.startsWith('product-badges--')
      ) || ''
    );
  }

  /**
   * Find a Ritual badge wrapper that can be used as the style source.
   *
   * Preference order:
   * 1. Native badge wrapper inside the current card gallery
   * 2. Another native product-card badge wrapper elsewhere on the page
   *
   * The fallback allows a custom badge on a full-price product to inherit the
   * active badge Theme settings even when that particular card has no native
   * SALE or SOLD OUT badge.
   *
   * @param {HTMLElement} gallery
   * @returns {HTMLElement|null}
   */
  function findNativeBadgeStyleSource(gallery) {
    const localWrapper = gallery.querySelector(
      ':scope > .product-badges:not(.bandholz-product-badges)'
    );

    if (localWrapper instanceof HTMLElement) {
      return localWrapper;
    }

    const pageWrapper = document.querySelector(
      [
        'product-card .card-gallery',
        'product-card [ref="cardGallery"]'
      ].join(', ')
    );

    if (pageWrapper instanceof HTMLElement) {
      const fallbackWrapper = pageWrapper.querySelector(
        ':scope > .product-badges:not(.bandholz-product-badges)'
      );

      if (fallbackWrapper instanceof HTMLElement) {
        return fallbackWrapper;
      }
    }

    const anyNativeWrapper = document.querySelector(
      '.product-badges:not(.bandholz-product-badges)'
    );

    return anyNativeWrapper instanceof HTMLElement
      ? anyNativeWrapper
      : null;
  }

  /**
   * Build a product-badge wrapper matching Ritual's native badge structure.
   *
   * Ritual stores the merchant-selected badge presentation on its wrapper
   * through inline CSS custom properties, including:
   * • Border radius
   * • Font family
   * • Font weight
   * • Text transform
   * • Position
   *
   * Copying the native style attribute allows the Bandholz badge to continue
   * following current and future Theme settings.
   *
   * @param {HTMLElement} badge
   * @param {string} fallbackBadgePosition
   * @param {HTMLElement|null} nativeBadgeWrapper
   * @returns {HTMLDivElement}
   */
  function createProductBadgeWrapper(
    badge,
    fallbackBadgePosition,
    nativeBadgeWrapper = null
  ) {
    const wrapper = document.createElement('div');

    const fallbackPositionClass =
      `product-badges--${fallbackBadgePosition || 'top-right'}`;

    wrapper.className = [
      'product-badges',
      fallbackPositionClass,
      'bandholz-product-badges'
    ].join(' ');

    if (nativeBadgeWrapper instanceof HTMLElement) {
      const nativeStyle =
        nativeBadgeWrapper.getAttribute('style');

      if (nativeStyle) {
        wrapper.setAttribute('style', nativeStyle);
      }

      const nativePositionClass =
        getBadgePositionClass(nativeBadgeWrapper);

      if (nativePositionClass) {
        wrapper.classList.remove(fallbackPositionClass);
        wrapper.classList.add(nativePositionClass);
      }
    }

    wrapper.appendChild(badge);

    return wrapper;
  }

  /**
   * Move server-rendered Bandholz custom badges into their card galleries.
   *
   * SOLD OUT remains controlled by Ritual. The Custom Liquid block only
   * renders a custom badge when the product is available.
   *
   * @param {Document|Element} root
   */
  function initializeProductCardBadges(root = document) {
    const markers = [];

    if (
      root instanceof Element &&
      root.matches('.bandholz-product-card-customizations')
    ) {
      markers.push(root);
    }

    if ('querySelectorAll' in root) {
      markers.push(
        ...root.querySelectorAll(
          '.bandholz-product-card-customizations'
        )
      );
    }

    for (const marker of markers) {
      if (!(marker instanceof HTMLElement)) {
        continue;
      }

      const productCard = marker.closest('product-card');

      const badge = marker.querySelector(
        '.bandholz-product-card-custom-badge'
      );

      if (
        !(productCard instanceof HTMLElement) ||
        !(badge instanceof HTMLElement)
      ) {
        continue;
      }

      if (
        badge.dataset.bandholzBadgeInitialized === 'true'
      ) {
        continue;
      }

      const gallery =
        productCard.querySelector('[ref="cardGallery"]') ||
        productCard.querySelector('.card-gallery');

      if (!(gallery instanceof HTMLElement)) {
        continue;
      }

      const nativeBadgeWrappers = Array.from(
        gallery.querySelectorAll(
          ':scope > .product-badges:not(.bandholz-product-badges)'
        )
      ).filter(
        (wrapper) => wrapper instanceof HTMLElement
      );

      const nativeBadgeStyleSource =
        nativeBadgeWrappers[0] ||
        findNativeBadgeStyleSource(gallery);

      /*
       * Hide only the native badge wrappers in the same card.
       *
       * The Custom Liquid block is only rendered for available products, so
       * these wrappers would represent a native SALE badge rather than the
       * higher-priority SOLD OUT badge.
       */
      for (const nativeWrapper of nativeBadgeWrappers) {
        nativeWrapper.setAttribute('hidden', '');
      }

      const fallbackBadgePosition =
        marker.dataset.bandholzBadgePosition ||
        'top-right';

      const customBadgeWrapper =
        createProductBadgeWrapper(
          badge,
          fallbackBadgePosition,
          nativeBadgeStyleSource
        );

      gallery.appendChild(customBadgeWrapper);

      badge.dataset.bandholzBadgeInitialized = 'true';
    }
  }

  /**
   * Read a possible Shopify product ID from a resource card.
   *
   * Different Ritual versions may expose it on the resource card itself or
   * on a nested product-related element.
   *
   * @param {HTMLElement} card
   * @returns {string}
   */
  function getResourceCardProductId(card) {
    const directId =
      card.dataset.productId ||
      card.getAttribute('data-product-id');

    if (directId) {
      return String(directId);
    }

    const productIdElement =
      card.querySelector('[data-product-id]');

    if (productIdElement instanceof HTMLElement) {
      return String(
        productIdElement.dataset.productId ||
        productIdElement.getAttribute('data-product-id') ||
        ''
      );
    }

    return '';
  }

  /**
   * Read the product path from a resource-card link.
   *
   * @param {HTMLElement} card
   * @returns {string}
   */
  function getResourceCardProductPath(card) {
    const productLink =
      card.querySelector('.resource-card__link') ||
      card.querySelector('a[href*="/products/"]');

    if (!(productLink instanceof HTMLAnchorElement)) {
      return '';
    }

    try {
      return new URL(
        productLink.href,
        window.location.origin
      ).pathname;
    } catch {
      return productLink.getAttribute('href') || '';
    }
  }

  /**
   * Determine whether a Header resource card represents the configured
   * Custom Fragrance Bundle.
   *
   * Product ID is checked first.
   * The product URL handle is used as the fallback.
   *
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  function isBundleResourceCard(card) {
    const resourceProductId =
      getResourceCardProductId(card);

    if (
      resourceProductId &&
      resourceProductId === config.bundleProductId
    ) {
      return true;
    }

    const expectedPath =
      `/products/${config.bundleProductHandle}`;

    const resourcePath =
      getResourceCardProductPath(card);

    return (
      resourcePath === expectedPath ||
      resourcePath.startsWith(`${expectedPath}?`) ||
      resourcePath.startsWith(`${expectedPath}/`)
    );
  }

  /**
   * Create the fixed bundle-card savings message.
   *
   * @returns {HTMLSpanElement}
   */
  function createBundleCardMessage() {
    const message = document.createElement('span');

    message.className =
      'bandholz-bundle-card-message';

    const accessibleLabel =
      document.createElement('span');

    accessibleLabel.className =
      'visually-hidden';

    accessibleLabel.textContent =
      'Pricing: ';

    message.appendChild(accessibleLabel);
    message.append(config.bundleCardMessage);

    return message;
  }

  /**
   * Identify the bundle product inside Header resource cards and replace its
   * native price display with the fixed Bandholz savings message.
   *
   * @param {Document|Element} root
   */
  function initializeHeaderBundleCard(root = document) {
    const resourceCards = [];

    if (
      root instanceof Element &&
      root.matches('.resource-card')
    ) {
      resourceCards.push(root);
    }

    if ('querySelectorAll' in root) {
      resourceCards.push(
        ...root.querySelectorAll('.resource-card')
      );
    }

    for (const card of resourceCards) {
      if (!(card instanceof HTMLElement)) {
        continue;
      }

      if (
        card.dataset.bandholzBundleCard === 'true'
      ) {
        continue;
      }

      if (!isBundleResourceCard(card)) {
        continue;
      }

      card.dataset.bandholzBundleCard = 'true';

      const content =
        card.querySelector('.resource-card__content') ||
        card;

      if (!(content instanceof HTMLElement)) {
        continue;
      }

      if (
        content.querySelector(
          '.bandholz-bundle-card-message'
        )
      ) {
        continue;
      }

      content.appendChild(
        createBundleCardMessage()
      );
    }
  }

  /**
   * Initialize all Bandholz storefront customizations.
   *
   * @param {Document|Element} root
   */
  function initializeBandholzCustomizations(
    root = document
  ) {
    initializeProductCardBadges(root);
    initializeHeaderBundleCard(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeBandholzCustomizations();
      },
      { once: true }
    );
  } else {
    initializeBandholzCustomizations();
  }

  /**
   * Reinitialize sections loaded or refreshed by the Shopify theme editor.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (event.target instanceof Element) {
        initializeBandholzCustomizations(
          event.target
        );
      }
    }
  );

  /**
   * Product grids, filtering, search interfaces and Header menus can insert
   * or hydrate cards after the initial page load.
   */
  const observer = new MutationObserver(
    (mutations) => {
      for (const mutation of mutations) {
        for (const addedNode of mutation.addedNodes) {
          if (!(addedNode instanceof Element)) {
            continue;
          }

          const isRelevantNode =
            addedNode.matches(
              [
                '.bandholz-product-card-customizations',
                '.resource-card'
              ].join(', ')
            );

          const containsRelevantNode =
            addedNode.querySelector(
              [
                '.bandholz-product-card-customizations',
                '.resource-card'
              ].join(', ')
            );

          if (
            isRelevantNode ||
            containsRelevantNode
          ) {
            initializeBandholzCustomizations(
              addedNode
            );
          }
        }
      }
    }
  );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();